import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { buildPlayHref } from "../util/detail-url";
import { MediaTile } from "../components/MediaTile";
import { useSelection } from "../components/SelectionContext";
import { SelectionToolbar, SelectModeToggle } from "../components/SelectionToolbar";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PlaylistDetailProps {
  id: number;
  isAdmin: boolean;
}

export function PlaylistDetail({ id, isAdmin }: PlaylistDetailProps) {
  const sel = useSelection();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["playlists", id], queryFn: () => api.getPlaylist(id) });

  const removeItem = useMutation({
    mutationFn: ({ mediaId }: { mediaId: number }) => api.removeFromPlaylist(id, mediaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["playlists", id] }),
  });

  // Reorder always sets a single item's `position`. Two-step swaps for
  // ±1 moves keep gaps from drifting wide; "move to top/bottom" use a
  // value beyond the current min/max so the new placement sorts where
  // expected. The orderBy on the server is (position ASC, id ASC), so
  // momentary position duplicates during a swap don't break ordering.
  const reorder = useMutation({
    mutationFn: (calls: Array<{ mediaId: number; position: number }>) =>
      Promise.all(calls.map((c) => api.reorderPlaylistItem(id, c.mediaId, c.position))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["playlists", id] }),
  });

  // Bulk reorder used by the drag-and-drop UI. Renumbers all items
  // 0..N in one atomic server call so a drop doesn't race against the
  // single-item PATCH endpoint or fight pending optimistic updates.
  const reorderAll = useMutation({
    mutationFn: (mediaIds: number[]) => api.reorderPlaylistAll(id, mediaIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["playlists", id] }),
  });

  // Sensors: drag starts only after the user has either moved 8 pixels
  // (pointer) or held for 250ms (touch). Both thresholds preserve the
  // tile's normal tap/click — a quick tap still opens the detail view,
  // a long-press on mobile begins reordering. Tolerance of 5px lets a
  // long-press wobble slightly without canceling.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  function onDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    const mediaIds = reorderedItems.map((it) => it.id);
    // Optimistic cache write so the grid snaps to the new order
    // immediately. onSuccess invalidates so the server-confirmed truth
    // overrides if anything diverges.
    qc.setQueryData(["playlists", id], (cur: typeof q.data) =>
      cur
        ? {
            ...cur,
            items: reorderedItems.map((it, idx) => ({ ...it, position: idx })),
          }
        : cur,
    );
    reorderAll.mutate(mediaIds);
  }

  // Reorder mode is an explicit toggle so casual browsing of a long
  // playlist isn't disrupted: outside the mode, tiles scroll and tap
  // normally; inside, tiles have touch-action: none and the long-press
  // / pointer-move thresholds activate drag-to-reorder. Without this
  // opt-in, mobile users couldn't scroll a viewport-filling grid (the
  // touch-action: none every tile needs for the TouchSensor would
  // intercept vertical pan).
  const [reorderMode, setReorderMode] = useState(false);

  const items = q.data?.items ?? [];
  useEffect(() => {
    sel.setVisibleIds(items.map((i) => i.id));
  }, [items, sel]);

  if (q.isPending) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (q.isError) return <div className="p-8 text-red-300">Error: {String(q.error)}</div>;
  const pl = q.data;
  const isOwner = pl.isOwner;
  const isPublic = pl.isPublic;
  const set = `playlist:${id}`;

  const firstId = items[0]?.id;

  return (
    <>
      <SelectionToolbar isAdmin={isAdmin} hasFilter={false} />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <a href="/playlists" className="text-sm text-zinc-400 hover:text-zinc-200">
              ← back to playlists
            </a>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <h2 className="text-2xl font-semibold">{pl.name}</h2>
              {isPublic && (
                <span className="rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-200">
                  Public
                </span>
              )}
              {!isOwner && (
                <span className="text-xs text-zinc-500">
                  read-only
                </span>
              )}
            </div>
            {pl.description && <p className="text-sm text-zinc-400">{pl.description}</p>}
            <p className="mt-1 text-xs text-zinc-500">
              {items.length} item{items.length === 1 ? "" : "s"}
            </p>
            {isOwner && <OwnerControls id={id} initialIsPublic={isPublic} />}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isOwner && <SelectModeToggle />}
            {isOwner && items.length > 1 && (
              <button
                onClick={() => setReorderMode((v) => !v)}
                className={
                  "rounded px-3 py-1.5 text-sm " +
                  (reorderMode
                    ? "bg-amber-700 text-amber-100 hover:bg-amber-600"
                    : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700")
                }
                title={
                  reorderMode
                    ? "Exit reorder mode — tiles return to normal tap/scroll behavior"
                    : "Reorder mode — long-press a tile on mobile or drag on desktop to move it"
                }
              >
                {reorderMode ? "✓ Done reordering" : "🔀 Reorder"}
              </button>
            )}
            {firstId != null && (
              <>
                <a
                  href={buildPlayHref({ id: firstId, set })}
                  className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-600"
                  title="Auto-advance through the playlist — photos slide, videos autoplay"
                >
                  ▶ Play playlist
                </a>
                <a
                  href={buildPlayHref({ id: firstId, set, shuffle: true })}
                  className="rounded bg-violet-700 px-3 py-1.5 text-sm text-violet-100 hover:bg-violet-600"
                  title="Play random items from this playlist, endlessly"
                >
                  🎲 Random
                </a>
              </>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded border border-zinc-800 bg-zinc-900 p-8 text-center text-zinc-500">
            This playlist is empty. Right-click any media item and choose "Add to playlist…", or use
            multi-select on a grid page.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={reorderMode ? onDragEnd : undefined}
          >
            <SortableContext items={items.map((it) => it.id)} strategy={rectSortingStrategy}>
              <div
                className={
                  "grid gap-2 " +
                  (reorderMode
                    ? "rounded border-2 border-dashed border-amber-700/50 bg-amber-900/5 p-2"
                    : "")
                }
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
              >
                {items.map((item, i) => {
                  const isFirst = i === 0;
                  const isLast = i === items.length - 1;
                  const minPos = items[0]?.position ?? 0;
                  const maxPos = items[items.length - 1]?.position ?? 0;
                  return (
                    <SortableTile
                      key={item.id}
                      item={item}
                      setParam={set}
                      isAdmin={isAdmin}
                      index={i}
                      reorderable={reorderMode && isOwner}
                      extraMenuItems={
                        isOwner
                          ? [
                              {
                                label: "▲▲ Move to top",
                                onClick: () =>
                                  reorder.mutate([{ mediaId: item.id, position: minPos - 1 }]),
                                disabled: isFirst,
                                divider: true,
                              },
                              {
                                label: "▲ Move earlier",
                                onClick: () => {
                                  const prev = items[i - 1]!;
                                  reorder.mutate([
                                    { mediaId: item.id, position: prev.position },
                                    { mediaId: prev.id, position: item.position },
                                  ]);
                                },
                                disabled: isFirst,
                              },
                              {
                                label: "▼ Move later",
                                onClick: () => {
                                  const next = items[i + 1]!;
                                  reorder.mutate([
                                    { mediaId: item.id, position: next.position },
                                    { mediaId: next.id, position: item.position },
                                  ]);
                                },
                                disabled: isLast,
                              },
                              {
                                label: "▼▼ Move to bottom",
                                onClick: () =>
                                  reorder.mutate([{ mediaId: item.id, position: maxPos + 1 }]),
                                disabled: isLast,
                              },
                              {
                                label: "Remove from playlist",
                                onClick: () => removeItem.mutate({ mediaId: item.id }),
                                danger: true,
                                divider: true,
                              },
                            ]
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
}

interface SortableTileProps {
  item: {
    id: number;
    kind: "photo" | "video";
    relativePath: string;
    width: number | null;
    height: number | null;
    durationMs: number | null;
    takenAt: string | null;
    sizeBytes: number;
    thumbUrl: string;
    posterUrl: string | null;
    isAnimated: boolean;
    originalUrl: string;
  };
  setParam: string;
  isAdmin: boolean;
  index: number;
  reorderable: boolean;
  extraMenuItems: React.ComponentProps<typeof MediaTile>["extraMenuItems"];
}

/**
 * Wraps a MediaTile with @dnd-kit's useSortable. When `reorderable` is
 * false, listeners and touch-action are NOT attached, so the tile
 * behaves exactly like any other (tap opens detail, scroll works on
 * mobile). When `reorderable` is true, the tile takes touch-action:
 * none and the activation constraints on the DndContext sensors
 * decide drag vs tap (8px pointer move or 250ms touch hold). The
 * mode toggle lives in the page header.
 *
 * While actively dragging, the tile fades to 40% opacity so the user
 * can see the drop target outline through it. CSS transitions handle
 * the other items reflowing around the gap.
 */
function SortableTile({
  item,
  setParam,
  isAdmin,
  index,
  reorderable,
  extraMenuItems,
}: SortableTileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !reorderable,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: reorderable ? (isDragging ? "grabbing" : "grab") : undefined,
    touchAction: reorderable ? "none" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(reorderable ? attributes : {})}
      {...(reorderable ? listeners : {})}
    >
      <MediaTile
        item={item}
        setParam={setParam}
        isAdmin={isAdmin}
        index={index}
        aspect="1 / 1"
        extraMenuItems={extraMenuItems}
      />
    </div>
  );
}

function OwnerControls({ id, initialIsPublic }: { id: number; initialIsPublic: boolean }) {
  const qc = useQueryClient();
  // Local state tracks the user's intent; useEffect resyncs from the prop
  // when the server-confirmed value lands. Disabling while pending blocks
  // double-clicks from racing two opposite mutations.
  const [checked, setChecked] = useState(initialIsPublic);
  useEffect(() => setChecked(initialIsPublic), [initialIsPublic]);
  const update = useMutation({
    mutationFn: (body: { isPublic?: boolean }) => api.updatePlaylist(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["playlists", id] });
      qc.invalidateQueries({ queryKey: ["playlists"] });
      qc.invalidateQueries({ queryKey: ["playlists", "public"] });
    },
  });
  return (
    <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs text-zinc-400">
      <input
        type="checkbox"
        checked={checked}
        disabled={update.isPending}
        onChange={(e) => {
          const next = e.target.checked;
          setChecked(next);
          update.mutate({ isPublic: next });
        }}
      />
      Public — anyone with library access can view this playlist
    </label>
  );
}
